/**
 * Universal Authenticated API Service
 * All services should use this for API calls
 */

import { getCurrentConfig } from '../config';
import { logger } from '../utils/logging/logger';

/**
 * Universal authenticated fetch - works with or without authentication
 */
export async function authenticatedFetch(
  endpoint: string, 
  options: RequestInit = {},
  getAccessToken?: () => Promise<string | null>
): Promise<Response> {
  const config = getCurrentConfig();
  const url = `${config.apiBaseUrl}${endpoint}`;
  
  logger.api('Making API request to:', url);
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...options.headers as Record<string, string>,
  };

  // Simple auth check - if auth is disabled, skip all token logic
  const authEnabled = process.env.REACT_APP_ENABLE_AUTHENTICATION === 'true';
  
  if (authEnabled && getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        logger.auth('Auth enabled: Using token');
      }
    } catch (error) {
      console.warn('Auth enabled but token acquisition failed:', error);
    }
  } else {
    logger.auth('Auth disabled: No token needed');
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * JSON version of authenticated fetch
 */
export async function authenticatedFetchJson<T = any>(
  endpoint: string, 
  options: RequestInit = {},
  getAccessToken?: () => Promise<string | null>
): Promise<T> {
  const response = await authenticatedFetch(endpoint, options, getAccessToken);
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}
